import { useState, useEffect } from 'react'
import { supabase } from '../lib/supabaseClient'

// ============================================================
// useContacts - Manages the contacts/address book
// Same pattern as useGigs but for contact data
// Provides add, update, delete, and fetch for contacts
// ============================================================

export function useContacts() {

  const [contacts, setContacts] = useState([]) // [] = empty list to start
  const [loading, setLoading] = useState(true) // true = still loading
  const [error, setError] = useState(null)     // null = no error

  // -------------------------------------------------------
  // Auto-fetch contacts when the hook is first used
  // -------------------------------------------------------
  useEffect(() => {
    fetchContacts()
    // load contacts immediately when Contacts page opens
  }, [])
  // [] = only run once on first render


  // -------------------------------------------------------
  // fetchContacts - Loads all contacts from the database
  // Filters by current user so you only see your own contacts
  // -------------------------------------------------------
  const fetchContacts = async () => {

    try {
      setLoading(true)  // start the loading spinner

      setError(null)    // clear any previous errors before new attempt

      const { data: { user }, error: userError } = await supabase.auth.getUser()
      // get who is logged in
      // data: { user } = dig into response.data and pull out user
      // error: userError = rename error to avoid variable name clash

      if (userError) throw userError   // couldnt identify the user
      if (!user) throw new Error('Not authenticated') // nobody logged in

      const { data, error: fetchError } = await supabase
        // error: fetchError = rename to avoid clash with outer error state
        .from('contacts')   // go to the contacts table
        .select('*')        // get all columns
        .eq('user_id', user.id)  // only this users contacts
        .order('created_at', { ascending: false })
        // sort by created_at column
        // ascending: false = newest contacts appear first
        // { ascending: false } is the options object for .order()

      if (fetchError) throw fetchError

      setContacts(data || [])
      // data || [] = use data if it exists, empty array if null
      // handles the case where user has no contacts yet

    } catch (err) {
      setError(err.message)
      console.error('Error fetching contacts:', err)

    } finally {
      setLoading(false)
      // always runs - always turn off spinner
    }
  }


  // -------------------------------------------------------
  // addContact - Saves a new contact to the database
  // Validates that name exists before attempting save
  // -------------------------------------------------------
  const addContact = async (formData) => {
    // formData = { name, pronouns, instagram, facebook, location }
    // all fields except name are optional

    try {
      setError(null)

      const { data: { user }, error: userError } = await supabase.auth.getUser()
      if (userError) throw userError
      if (!user) throw new Error('Not authenticated')

      // ---- VALIDATION ----
      // Check required fields BEFORE hitting the database
      // Saves a wasted network request if data is invalid
      if (!formData.name || formData.name.trim() === '') {
        // !formData.name = name is null or undefined
        // .trim() removes whitespace from both ends
        // === '' checks if its empty after removing spaces
        // so "   " (just spaces) would also fail this check
        throw new Error('Name is required')
        // this jumps to catch immediately
      }

      const { data: newContact, error: insertError } = await supabase
        // data: newContact = rename 'data' to 'newContact' for clarity
        .from('contacts')
        .insert([{
          user_id: user.id,                        // who owns this contact
          name: formData.name,                     // required field
          pronouns: formData.pronouns || null,     // || null = if empty string, save null
          instagram: formData.instagram || null,   // same pattern for all optional fields
          facebook: formData.facebook || null,
          location: formData.location || null,
          created_at: new Date().toISOString()
          // new Date() = right now as a Date object
          // .toISOString() = convert to "2025-02-15T14:30:00.000Z" format
          // this is the standard format databases expect
        }])
        .select()
        // return the inserted row so we can add it to local state

      if (insertError) throw insertError

      if (!newContact || newContact.length === 0) throw new Error('Failed to save contact')
      // newContact.length === 0 = empty array was returned
      // this is an extra safety check - sometimes insert "succeeds" but returns nothing

      setContacts([newContact[0], ...contacts])
      // newContact[0] = the new contact (first item of the returned array)
      // ...contacts = spread all existing contacts after it
      // [newContact[0], ...contacts] = new contact at the FRONT of the list
      // appears at top because contacts are sorted newest first

      return newContact[0]
      // send the new contact back to the form

    } catch (err) {
      setError(err.message)
      console.error('Error adding contact:', err)
      throw err
    }
  }


  // -------------------------------------------------------
  // deleteContact - Removes a contact from the database
  // -------------------------------------------------------
  const deleteContact = async (contactId) => {
    // contactId = the UUID of the contact to delete

    try {
      setError(null)

      const { error: deleteError } = await supabase
        // error: deleteError = rename to avoid clash
        .from('contacts')
        .delete()
        .eq('id', contactId)
        // WHERE id = the specific contact
        // critical - without this, all contacts would be deleted

      if (deleteError) throw deleteError

      setContacts(contacts.filter(c => c.id !== contactId))
      // .filter() keeps items that return true
      // c => = for each contact temporarily called 'c'
      // c.id !== contactId = keep if NOT the deleted one
      // !== means "not strictly equal"
      // result = list without the deleted contact

    } catch (err) {
      setError(err.message)
      console.error('Error deleting contact:', err)
      throw err
    }
  }


  // -------------------------------------------------------
  // updateContact - Changes fields on an existing contact
  // -------------------------------------------------------
  const updateContact = async (contactId, updates) => {
    // contactId = which contact to update
    // updates = object with only the changed fields
    //   like { name: "New Name", location: "Charlotte" }

    try {
      setError(null)

      const { data: updatedContact, error: updateError } = await supabase
        // data: updatedContact = rename for clarity
        .from('contacts')
        .update(updates)         // only fields in updates are changed
        .eq('id', contactId)     // find the specific contact
        .select()                // return the updated row

      if (updateError) throw updateError

      if (!updatedContact || updatedContact.length === 0)
        throw new Error('Failed to update contact')
      // safety check - make sure we got the updated data back

      setContacts(contacts.map(c =>
        c.id === contactId ? updatedContact[0] : c
      ))
      // .map() goes through every contact
      // c => = each contact temporarily called 'c'
      // c.id === contactId ? updatedContact[0] : c = ternary
      //   if this is the updated contact = use new data
      //   if not = keep original
      // replaces one item in the list in-place

      return updatedContact[0]
      // send updated contact back to the form

    } catch (err) {
      setError(err.message)
      console.error('Error updating contact:', err)
      throw err
    }
  }


  // Return everything the Contacts page needs
  return {
    contacts,       // array of contact objects
    loading,        // boolean - true while fetching
    error,          // error message or null
    addContact,     // function(formData) - creates contact
    deleteContact,  // function(contactId) - removes contact
    updateContact,  // function(contactId, updates) - edits contact
    fetchContacts   // function() - manually reload
  }
}